import { Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFolderUp, IconPencil, IconTrash } from '@tabler/icons-react';
import { DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextInputModal } from 'src/Components/Modals/TextInputModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useTranslateFolderName } from 'src/Hooks/Format/useTranslateFolderName';
import { useRenameStorageItem } from 'src/Hooks/Mutations/useRenameStorageItem';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { getDirectoryIcon } from 'src/Utils/getDirectoryIcon';

import styles from './DocumentFolders.module.scss';
import { Props } from './DocumentFolders.types';

const isFileDrag = (event: DragEvent<HTMLDivElement>) =>
  Array.from(event.dataTransfer.types).includes('Files');

interface FolderCardProps {
  folder: { label: string; id: string };
  translatedLabel: string;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
  renameDisabled: boolean;
  canUpload: boolean;
  onUpload: (files: File[]) => void;
}

const FolderCard = ({
  folder,
  translatedLabel,
  onClick,
  onRename,
  onDelete,
  renameDisabled,
  canUpload,
  onUpload,
}: FolderCardProps) => {
  const { t } = useTranslation();
  const [isDropTarget, setIsDropTarget] = useState(false);
  const dragCounter = useRef(0);
  const DirectoryIcon = getDirectoryIcon(folder.label);

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!canUpload || !isFileDrag(event)) return;
    event.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDropTarget(true);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canUpload || !isFileDrag(event)) return;
    event.preventDefault();
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!canUpload || !isFileDrag(event)) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDropTarget(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!canUpload || !isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current = 0;
    setIsDropTarget(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      onUpload(droppedFiles);
    }
  };

  const card = (
    <div
      className={`${styles.folder} ${isDropTarget ? styles.folderDropTarget : ''}`}
      onClick={onClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DirectoryIcon size={22} className={styles.icon} />
      <p className={styles.folderName}>{translatedLabel}</p>
      <RowActions
        actions={[
          {
            key: 'rename',
            label: t('documents.folder.menu.rename', 'Renommer'),
            icon: <IconPencil size={18} />,
            disabled: renameDisabled,
            onClick: onRename,
          },
          {
            key: 'delete',
            label: t('documents.folder.delete', 'Supprimer'),
            icon: <IconTrash size={18} />,
            variant: 'danger',
            onClick: onDelete,
          },
        ]}
      />
    </div>
  );

  if (!isDropTarget) {
    return card;
  }

  return (
    <Tooltip
      label={t('documents.folder.dropTarget', 'Ajouter dans {{name}}', { name: translatedLabel })}
      position="top"
      withArrow
      opened
    >
      {card}
    </Tooltip>
  );
};

export const DocumentFolders = ({
  folders,
  onFolderClick,
  onGoToParent,
  showGoToParentFolder,
  onDeleteFolder,
  onUploadToFolder,
  canUpload,
}: Props) => {
  const { t } = useTranslation();

  const translateFolderName = useTranslateFolderName();

  const [renameModalOpen, { open: openRenameActionModal, close: closeRenameActionModal }] =
    useDisclosure();
  const [actionId, setActionId] = useState<string | undefined>();

  const { mutate: rename, isLoading } = useRenameStorageItem({
    onSettled: () => {
      closeRenameActionModal();
    },
  });

  const assetAboveQuotas = useIsAssetAboveQuotas();

  return (
    <div className={styles.container}>
      <TextInputModal
        open={renameModalOpen}
        onClose={closeRenameActionModal}
        title={t('documents.folder.rename.title', 'Renommer')}
        subtitle={''}
        placeholder={t('documents.folder.rename.newName', 'Nouveau nom du dossier')}
        buttonText={t('documents.folder.rename.rename', 'Renommer')}
        onValidate={text => {
          if (actionId) {
            rename({ itemId: actionId, name: text });
          }
        }}
        loading={isLoading}
        defaultValue={folders.find(folder => folder.id === actionId)?.label}
      />
      {showGoToParentFolder && (
        <div className={styles.folder} onClick={() => onGoToParent()}>
          <IconFolderUp size={22} />
          <p className={styles.folderName}> {t('documents.topFolder', 'Dossier parent')}</p>
        </div>
      )}
      {folders.map(folder => (
        <FolderCard
          key={folder.id}
          folder={folder}
          translatedLabel={translateFolderName(folder.label)}
          onClick={() => onFolderClick(folder.label)}
          onRename={() => {
            setActionId(folder.id);
            openRenameActionModal();
          }}
          onDelete={() => onDeleteFolder(folder.id)}
          renameDisabled={assetAboveQuotas}
          canUpload={canUpload}
          onUpload={files => onUploadToFolder(folder.label, files)}
        />
      ))}
    </div>
  );
};

export default DocumentFolders;
